/**
 * Survival of an in-page TRANSLATION (kitecorp/transferify-ui#2580).
 *
 * Chrome's "Translate this page" rewrites the document under React: every text node is replaced by
 * `<font><font>translated text</font></font>`, so the text node React's fiber still records as a
 * child of some element now lives one level deeper, under a wrapper React never created. The next
 * conditional re-render — pressing "Log in" and then "Email me a login code instead" is the
 * shortest route to one — asks the old parent to remove the old child and the browser answers
 * `NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child
 * of this node`. React has no recovery for that: the error reaches the router boundary and the
 * whole page becomes "This page could not be loaded". A translated visitor's app is simply dead.
 *
 * React declines to fix it in the library (facebook/react#11538, "Make React resilient to DOM
 * mutations from Google Translate" — closed without a general fix), and recommends exactly this
 * application-side workaround: make the two mutations React uses for conditional children total
 * rather than throwing, so the app stays alive.
 *
 * WHAT IS TRADED, stated plainly because absorbing is not repairing. The `<font>` wrapper holding
 * the translated copy is still a child of the original parent, so an absorbed `removeChild` leaves
 * that copy ON SCREEN: a translated page can keep showing a line React meant to delete — the login
 * modal keeps a mixture of both views' headings, which `e2e/translated-dom.spec.ts` walks through in
 * full. An absorbed `insertBefore` appends, so new content lands at the END of its container rather
 * than in position. Neither is repairable from here without re-implementing translation; both are
 * the accepted price of the app running at all, and a real translation session re-translates on
 * mutation, which repairs much of it.
 *
 * WHAT IS ALSO SWALLOWED. This patches `Node.prototype`, so from here on ANY wrong-parent
 * `removeChild`/`insertBefore` — ours, React's, or a third-party library's — stops throwing, and
 * production is given no signal at all (the warning below is development-only, because reporting it
 * needs an api endpoint this branch does not add). That is not hypothetical: `utils/staleHydrationTree.ts`
 * exists because a `<Seo>` unmount ran `removeChild` with a null parent and crashed, and with this
 * guard installed that crash would have been absorbed in silence and never investigated. Weigh that
 * before reaching for `Node.prototype` again.
 *
 * WHY NOT `<meta name="google" content="notranslate">` or `translate="no"`: it works by switching
 * translation OFF, for every visitor, on every page. We serve three brands across several
 * languages, and a reader who asked their browser for their own language would instead get an
 * untranslated page with no explanation. The owner decided the app must keep being translatable and
 * absorb the mutations instead.
 *
 * This is a deliberately NARROW patch of two `Node.prototype` methods, and it only diverges from
 * native behaviour in the case that natively throws. Plain module, no React: installed once from
 * the SSG entry's client-only callback, the same way `staleChunkRecovery` is.
 */
import {isDev} from "utils/ReactHelpers";

/** The one-time diagnostic. A constant so its single delivery can be asserted against this text. */
export const TRANSLATED_DOM_GUARD_WARNING = "A page translation moved DOM nodes React still owns; "
	+ "removeChild/insertBefore were absorbed instead of throwing (transferify-ui#2580).";

/**
 * Marks the patched methods. Kept ON THE FUNCTIONS rather than in a module variable so the
 * already-installed answer survives a second module instance and disappears the moment anything
 * restores the originals — a module flag would claim an install that is no longer there.
 */
const GUARD_MARKER = "__transferifyTranslatedDomGuard";

type GuardedMethod = {[GUARD_MARKER]?: true};

export type TranslatedDomGuardOptions = {
	/** Where the one-time diagnostic goes. Defaults to the console in development, nowhere in production. */
	warn?: (message: string) => void;
};

function warnInDevelopment(message: string): void {
	if (isDev()) {
		console.warn(message);
	}
}

/**
 * Patches `Node.prototype.removeChild` / `insertBefore` so a translated document cannot crash a
 * re-render. Returns whether this call installed them: `false` means either no DOM (the SSG
 * prerender) or an install that is already in place.
 */
export function installTranslatedDomGuard(options: TranslatedDomGuardOptions = {}): boolean {
	// Read lazily, never at module scope: the SSG prerender evaluates this module in Node, where
	// there is no `Node` constructor and nothing to patch.
	if (typeof Node === "undefined") {
		return false;
	}
	const prototype = Node.prototype;
	const nativeRemoveChild: typeof prototype.removeChild & GuardedMethod = prototype.removeChild;
	const nativeInsertBefore: typeof prototype.insertBefore & GuardedMethod = prototype.insertBefore;
	// BOTH are checked. Reading only one would let an asymmetric restore — anything that puts the
	// native `removeChild` back without the native `insertBefore` — capture an already-guarded
	// `insertBefore` as its "native" and wrap it a second time, while the marker still reported an
	// install in place.
	if (nativeRemoveChild[GUARD_MARKER] === true || nativeInsertBefore[GUARD_MARKER] === true) {
		return false;
	}
	/**
	 * `parentNode` read through the prototype accessor rather than off the node.
	 *
	 * `HTMLFormElement` is `[LegacyOverrideBuiltIns]`: a control NAMED `parentNode` inside a form
	 * becomes an own property that shadows the getter, so `form.parentNode` would answer the input and
	 * the guard would refuse a removal that was perfectly legitimate. Nothing in this app names a
	 * control from user input today, but this patch governs every removal in the document, including a
	 * third-party library's. jsdom does not implement that shadowing, so there is no unit test for it —
	 * a test would pass with or without this line.
	 */
	const parentNodeOf = Object.getOwnPropertyDescriptor(prototype, "parentNode")?.get;
	const parentOf = (node: Node): ParentNode | Node | null =>
		parentNodeOf === undefined ? node.parentNode : parentNodeOf.call(node) as Node | null;
	const warn = options.warn ?? warnInDevelopment;
	let warned = false;
	const warnOnce = (): void => {
		if (warned) {
			return;
		}
		warned = true;
		warn(TRANSLATED_DOM_GUARD_WARNING);
	};

	const guardedRemoveChild: typeof prototype.removeChild & GuardedMethod =
		function removeChild<T extends Node>(this: Node, child: T): T {
			if (parentOf(child) === this) {
				nativeRemoveChild.call(this, child);
			} else {
				warnOnce();
			}
			// Returned from both branches rather than from each call, because `Function.prototype.call`
			// erases the generic back to `Node` — and the answer is the same node either way: a removal
			// hands back what it removed, and the absorbed case hands back the node somebody else moved.
			return child;
		};
	const guardedInsertBefore: typeof prototype.insertBefore & GuardedMethod =
		function insertBefore<T extends Node>(this: Node, node: T, reference: Node | null): T {
			// A null reference already MEANS "append", and is not a translation artefact.
			if (reference !== null && parentOf(reference) !== this) {
				warnOnce();
				this.appendChild(node);
			} else {
				nativeInsertBefore.call(this, node, reference);
			}
			return node;
		};
	guardedRemoveChild[GUARD_MARKER] = true;
	guardedInsertBefore[GUARD_MARKER] = true;
	prototype.removeChild = guardedRemoveChild;
	prototype.insertBefore = guardedInsertBefore;
	return true;
}
